import * as Yup from 'yup'

import { useFormik } from 'formik'
import { useMutation } from '@apollo/client'
import {
  BeginLoginResult,
  IResBeginPasskeyAuthentication,
  IResBeginLogin,
  IResCompletePasskeyAuthentication,
  IResCompleteLogin,
  IResSendEmailTwoFactorCode,
  TokenAuth,
} from '../../types/auth'
import {
  BEGIN_PASSKEY_AUTHENTICATION,
  BEGIN_LOGIN,
  COMPLETE_PASSKEY_AUTHENTICATION,
  COMPLETE_LOGIN,
  SEND_EMAIL_TWO_FACTOR_CODE,
} from '../../gql/mutations/authMutations'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { useGetUser } from '../../store/userStore'
import { STORAGE_NAMES, VERSION_APP } from '../../helpers/consts'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import {
  getPasskeyAssertion,
  getPasskeyErrorMessage,
  getPasskeyUnavailableMessage,
  isPasskeySupported,
} from '../../helpers/passkeys'
import {
  motion,
  AnimatePresence,
  useReducedMotion,
  useMotionValue,
  useSpring,
  useTransform,
} from 'framer-motion'
import { Mail, Lock, Eye, EyeOff, Fingerprint, ShieldCheck } from 'lucide-react'

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .email('Wrong email format')
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Email is required'),
  password: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Password is required'),
})

const getErrorMessage = (error: any) => {
  let message = 'An error occurred. Please try again.'

  if (error?.message?.includes('Please enter valid credentials')) {
    return 'Please enter valid credentials'
  }

  if (error?.graphQLErrors?.length > 0) {
    return error.graphQLErrors[0].message
  }

  if (typeof error?.message === 'string') {
    return error.message.replace(/ApolloError.*/s, '').trim() || message
  }

  return message
}

export const LoginPage = () => {
  const [gqlBeginLogin, { loading: loadingBegin }] = useMutation<IResBeginLogin>(BEGIN_LOGIN)
  const [gqlCompleteLogin, { loading: loadingComplete }] =
    useMutation<IResCompleteLogin>(COMPLETE_LOGIN)
  const [gqlSendEmailCode, { loading: loadingEmailCode }] =
    useMutation<IResSendEmailTwoFactorCode>(SEND_EMAIL_TWO_FACTOR_CODE)
  const [gqlBeginPasskeyAuthentication, { loading: loadingPasskeyBegin }] =
    useMutation<IResBeginPasskeyAuthentication>(BEGIN_PASSKEY_AUTHENTICATION)
  const [gqlCompletePasskeyAuthentication, { loading: loadingPasskeyComplete }] =
    useMutation<IResCompletePasskeyAuthentication>(COMPLETE_PASSKEY_AUTHENTICATION)

  const login = useGetUser((s) => s.login)
  const navigate = useNavigate()
  const location = useLocation()

  const [showPass, setShowPass] = useState(false)
  const [challengeId, setChallengeId] = useState<string | null>(null)
  const [availableMethods, setAvailableMethods] = useState<string[]>([])
  const [selectedMethod, setSelectedMethod] = useState<'APP' | 'EMAIL'>('APP')
  const [emailDestination, setEmailDestination] = useState<string | null>(null)
  const [twoFactorCode, setTwoFactorCode] = useState('')
  const passkeySupported = useMemo(() => isPasskeySupported(), [])

  const isTwoFactorStep = Boolean(challengeId)
  const canUseEmailFallback = availableMethods.includes('EMAIL')
  const loading =
    loadingBegin || loadingComplete || loadingEmailCode || loadingPasskeyBegin || loadingPasskeyComplete

  const stepTitle = useMemo(() => {
    if (!isTwoFactorStep) return 'Sign In'
    return selectedMethod === 'EMAIL' ? 'Email Verification' : 'Authenticator Verification'
  }, [isTwoFactorStep, selectedMethod])

  const resetTwoFactorState = () => {
    setChallengeId(null)
    setAvailableMethods([])
    setSelectedMethod('APP')
    setEmailDestination(null)
    setTwoFactorCode('')
  }

  const handleAuthError = (error: any, setStatus: (message: string | null) => void) => {
    const message = getErrorMessage(error)
    const requiresPasswordChange = message.includes('You must change your password before signing in.')

    if (requiresPasswordChange) {
      const emailQuery = encodeURIComponent(formik.values.email || '')
      toast.info('You must change your password to continue.')
      navigate(`/reset-password?forced=1&email=${emailQuery}`)
      return
    }

    toast.error(message)
    setStatus(message)
  }

  const handleSuccessfulLogin = (auth: TokenAuth) => {
    login(auth)

    if (auth.user?.twoFactorRequired && !auth.user?.twoFactorEnabled) {
      toast.info('Two-factor authentication setup is required for your account.')
      navigate('/profile', { replace: true })
      return
    }

    const url = (location?.state as { prevUrl: string })?.prevUrl || '/'
    navigate(url)
  }

  const handleBeginLoginResult = (result?: BeginLoginResult | null, setStatus?: (message: string | null) => void) => {
    if (!result) {
      const message = 'Unable to start login. Please try again.'
      toast.error(message)
      setStatus?.(message)
      return
    }

    if (result.auth) {
      handleSuccessfulLogin(result.auth)
      return
    }

    if (result.requiresTwoFactor && result.challengeId) {
      setChallengeId(result.challengeId)
      setAvailableMethods(result.availableMethods || ['APP'])
      setSelectedMethod('APP')
      setEmailDestination(result.emailDestination || null)
      setTwoFactorCode('')
      setStatus?.(null)
      toast.info(result.message || 'Two-factor authentication is required.')
      return
    }

    const fallbackMessage = result.message || 'Unable to continue the login flow.'
    toast.error(fallbackMessage)
    setStatus?.(fallbackMessage)
  }

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema,
    onSubmit: async (values, { setStatus }) => {
      setStatus(null)

      if (isTwoFactorStep) {
        if (!twoFactorCode.trim()) {
          setStatus('Verification code is required')
          return
        }

        try {
          const response = await gqlCompleteLogin({
            variables: {
              challengeId: challengeId!,
              code: twoFactorCode.trim(),
              method: selectedMethod,
            },
          })

          const auth = response.data?.completeLogin?.auth
          if (!auth) {
            const message =
              response.data?.completeLogin?.message || 'Unable to complete login. Please try again.'
            toast.error(message)
            setStatus(message)
            return
          }

          handleSuccessfulLogin(auth)
        } catch (error) {
          handleAuthError(error, setStatus)
        }

        return
      }

      try {
        const response = await gqlBeginLogin({
          variables: {
            email: values.email,
            password: values.password,
          },
        })

        handleBeginLoginResult(response.data?.beginLogin, setStatus)
      } catch (error) {
        handleAuthError(error, setStatus)
      }
    },
  })

  const handleSendEmailCode = async () => {
    if (!challengeId) return

    formik.setStatus(null)

    try {
      const response = await gqlSendEmailCode({
        variables: {
          challengeId: challengeId!,
        },
      })

      const result = response.data?.sendEmailTwoFactorCode
      if (!result) {
        throw new Error('Unable to send the email verification code.')
      }

      if (!result.success) {
        toast.info(result.message || 'Please wait before requesting another code.')
        formik.setStatus(result.message || 'Please wait before requesting another code.')
        return
      }

      setSelectedMethod('EMAIL')
      setEmailDestination(result.emailDestination || emailDestination)
      setTwoFactorCode('')
      toast.success(result.message || 'Verification code sent by email.')
    } catch (error) {
      handleAuthError(error, formik.setStatus)
    }
  }

  const handlePasskeySignIn = async () => {
    const normalizedEmail = formik.values.email.trim().toLowerCase()

    if (!normalizedEmail) {
      formik.setFieldTouched('email', true)
      formik.setStatus('Email is required to use passkey sign-in.')
      return
    }

    if (!passkeySupported) {
      const message = getPasskeyUnavailableMessage()
      toast.info(message)
      formik.setStatus(message)
      return
    }

    formik.setStatus(null)

    try {
      const beginResponse = await gqlBeginPasskeyAuthentication({
        variables: {
          email: normalizedEmail,
        },
      })

      const result = beginResponse.data?.beginPasskeyAuthentication
      if (!result?.success || !result.challengeId || !result.publicKeyOptions) {
        const message = result?.message || 'Passkey sign-in is not available for this account.'
        toast.error(message)
        formik.setStatus(message)
        return
      }

      const credential = await getPasskeyAssertion(result.publicKeyOptions)
      const completeResponse = await gqlCompletePasskeyAuthentication({
        variables: {
          challengeId: result.challengeId,
          credential,
        },
      })

      const completeResult = completeResponse.data?.completePasskeyAuthentication
      if (!completeResult?.success || !completeResult?.auth) {
        const message = completeResult?.message || 'Unable to complete passkey sign-in.'
        toast.error(message)
        formik.setStatus(message)
        return
      }

      handleSuccessfulLogin(completeResult.auth)
    } catch (error) {
      const message = getPasskeyErrorMessage(error, 'Unable to sign in with passkey.')
      const requiresPasswordChange = message.includes(
        'You must change your password before signing in.'
      )

      if (requiresPasswordChange) {
        const emailQuery = encodeURIComponent(normalizedEmail)
        toast.info('You must change your password to continue.')
        navigate(`/reset-password?forced=1&email=${emailQuery}`)
        return
      }

      toast.error(message)
      formik.setStatus(message)
    }
  }

  // ── RingCentral widget ──────────────────────────────────────────────────────
  useEffect(() => {
    const $div = document.querySelector('#rc-widget') as HTMLDivElement
    if (!$div) return
    const str = localStorage.getItem(STORAGE_NAMES.RING_CENTRAL)
    if (str) {
      $div.style.opacity = '0'
      $div.style.pointerEvents = 'none'
    }

    return () => {
      if ($div) {
        $div.style.opacity = '1'
        $div.style.pointerEvents = 'initial'
      }
    }
  }, [])

  // ── Motion / animation setup ────────────────────────────────────────────────
  const prefersReducedMotion = useReducedMotion()
  const containerRef = useRef<HTMLDivElement>(null)
  const GLOW_SIZE = 700

  const rawX = useMotionValue(0)
  const rawY = useMotionValue(0)
  const springCfg = { stiffness: 70, damping: 22, mass: 0.6 }
  const smoothX = useSpring(rawX, springCfg)
  const smoothY = useSpring(rawY, springCfg)

  const glowX = useTransform(smoothX, (v) => v - GLOW_SIZE / 2)
  const glowY = useTransform(smoothY, (v) => v - GLOW_SIZE / 2)

  const p1x = useTransform(smoothX, (v) => (v - (containerRef.current?.clientWidth ?? 1440) / 2) * 0.042)
  const p1y = useTransform(smoothY, (v) => (v - (containerRef.current?.clientHeight ?? 900) / 2) * 0.028)
  const p2x = useTransform(smoothX, (v) => (v - (containerRef.current?.clientWidth ?? 1440) / 2) * 0.022)
  const p2y = useTransform(smoothY, (v) => (v - (containerRef.current?.clientHeight ?? 900) / 2) * 0.015)
  const p3x = useTransform(smoothX, (v) => (v - (containerRef.current?.clientWidth ?? 1440) / 2) * 0.010)
  const p3y = useTransform(smoothY, (v) => (v - (containerRef.current?.clientHeight ?? 900) / 2) * 0.007)

  const particles = useMemo(() =>
    Array.from({ length: 80 }, (_, i) => ({
      id: i,
      x: (i * 137.508) % 100,
      y: (i * 97.333 + i * 0.7) % 100,
      size: 1.5 + (i % 5) * 0.6,
      opacity: 0.13 + (i % 7) * 0.055,
      layer: i % 3,
      accent: i % 5 === 0,
    }))
    , [])

  const tiltX = useTransform(smoothY, (v) => {
    const h = containerRef.current?.clientHeight ?? 900
    return ((v - h / 2) / (h / 2)) * -4
  })
  const tiltY = useTransform(smoothX, (v) => {
    const w = containerRef.current?.clientWidth ?? 1440
    return ((v - w / 2) / (w / 2)) * 4
  })

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    rawX.set(el.clientWidth / 2)
    rawY.set(el.clientHeight / 2)
  }, [rawX, rawY])

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (prefersReducedMotion) return
      const rect = e.currentTarget.getBoundingClientRect()
      rawX.set(e.clientX - rect.left)
      rawY.set(e.clientY - rect.top)
    },
    [prefersReducedMotion, rawX, rawY]
  )

  const handleMouseLeave = useCallback(() => {
    if (prefersReducedMotion || !containerRef.current) return
    rawX.set(containerRef.current.clientWidth / 2)
    rawY.set(containerRef.current.clientHeight / 2)
  }, [prefersReducedMotion, rawX, rawY])

  const transition = prefersReducedMotion ? { duration: 0 } : { duration: 0.45, ease: [0.22, 1, 0.36, 1] }

  const cardVariants = {
    hidden: { opacity: 0, y: prefersReducedMotion ? 0 : 32, scale: prefersReducedMotion ? 1 : 0.97 },
    visible: { opacity: 1, y: 0, scale: 1, transition },
  }

  const containerVariants = {
    hidden: {},
    visible: { transition: { staggerChildren: prefersReducedMotion ? 0 : 0.07 } },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: prefersReducedMotion ? 0 : 12 },
    visible: { opacity: 1, y: 0, transition: prefersReducedMotion ? { duration: 0 } : { duration: 0.35 } },
  }

  const stepVariants = {
    enter: { opacity: 0, x: prefersReducedMotion ? 0 : 30 },
    center: { opacity: 1, x: 0, transition },
    exit: { opacity: 0, x: prefersReducedMotion ? 0 : -30, transition: { ...transition, duration: 0.25 } },
  }

  const isSubmitDisabled =
    formik.isSubmitting ||
    loading ||
    (!isTwoFactorStep && !formik.isValid) ||
    (isTwoFactorStep && !twoFactorCode.trim())

  return (
    <div
      className='login-revamp-root'
      ref={containerRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {/* ── Background ─────────────────────────────────────────────────────── */}
      <div className='lr-aurora' aria-hidden='true' />
      <div className='lr-dots' aria-hidden='true' />
      <div className='lr-blob lr-blob--1' aria-hidden='true' />
      <div className='lr-blob lr-blob--2' aria-hidden='true' />
      <div className='lr-blob lr-blob--3' aria-hidden='true' />
      <motion.div className='lr-cursor-glow' style={{ x: glowX, y: glowY }} aria-hidden='true' />

      {/* ── Parallax particle layers ────────────────────────────────────────── */}
      {!prefersReducedMotion && (
        <>
          <motion.div className='lr-particles' style={{ x: p1x, y: p1y }} aria-hidden='true'>
            {particles.filter(p => p.layer === 0).map(p => (
              <span key={p.id} className={`lr-particle${p.accent ? ' lr-particle--accent' : ''}`}
                style={{ left: `${p.x}%`, top: `${p.y}%`, width: p.size, height: p.size, opacity: p.opacity }} />
            ))}
          </motion.div>
          <motion.div className='lr-particles' style={{ x: p2x, y: p2y }} aria-hidden='true'>
            {particles.filter(p => p.layer === 1).map(p => (
              <span key={p.id} className={`lr-particle${p.accent ? ' lr-particle--accent' : ''}`}
                style={{ left: `${p.x}%`, top: `${p.y}%`, width: p.size, height: p.size, opacity: p.opacity }} />
            ))}
          </motion.div>
          <motion.div className='lr-particles' style={{ x: p3x, y: p3y }} aria-hidden='true'>
            {particles.filter(p => p.layer === 2).map(p => (
              <span key={p.id} className={`lr-particle${p.accent ? ' lr-particle--accent' : ''}`}
                style={{ left: `${p.x}%`, top: `${p.y}%`, width: p.size, height: p.size, opacity: p.opacity }} />
            ))}
          </motion.div>
        </>
      )}

      {/* ── Page content ───────────────────────────────────────────────────── */}
      <div className='lr-page'>

        {/* Heading: logo + title + subtitle */}
        <motion.div
          className='lr-heading'
          variants={containerVariants}
          initial='hidden'
          animate='visible'
        >
          <motion.img
            src='/media/tfi/logo_policytools.png'
            alt='Policy Tools'
            className='lr-logo'
            variants={itemVariants}
          />
          <motion.h1 className='lr-page-title' variants={itemVariants}>
            Welcome back to Policy Tools!
          </motion.h1>
          <motion.p className='lr-page-subtitle' variants={itemVariants}>
            The agile platform for your enterprise policies.
          </motion.p>
        </motion.div>

        {/* Split card */}
        <motion.div
          className='lr-card'
          variants={cardVariants}
          initial='hidden'
          animate='visible'
          style={prefersReducedMotion ? {} : { rotateX: tiltX, rotateY: tiltY }}
        >
          {/* ── Left: form ───────────────────────────────────────────────── */}
          <div className='lr-card-form'>
            <form
              onSubmit={formik.handleSubmit}
              noValidate
              id='kt_login_signin_form'
              aria-label='Sign in form'
            >
              {/* Step label */}
              <AnimatePresence mode='wait'>
                <motion.span
                  key={stepTitle}
                  className='lr-step-label'
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: prefersReducedMotion ? 0 : 0.2 }}
                >
                  {stepTitle}
                </motion.span>
              </AnimatePresence>

              {/* Error alert */}
              <AnimatePresence>
                {formik.status && (
                  <motion.div
                    className='lr-alert'
                    role='alert'
                    initial={{ opacity: 0, height: 0, marginBottom: 0 }}
                    animate={{ opacity: 1, height: 'auto', marginBottom: 20 }}
                    exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                    transition={{ duration: prefersReducedMotion ? 0 : 0.22 }}
                  >
                    {formik.status}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Step content */}
              <AnimatePresence mode='wait'>
                {!isTwoFactorStep ? (
                  <motion.div key='step-login' variants={stepVariants} initial='enter' animate='center' exit='exit'>
                    <motion.div variants={containerVariants} initial='hidden' animate='visible'>

                      {/* Email */}
                      <motion.div className='lr-form-group' variants={itemVariants}>
                        <label className='lr-label' htmlFor='login-email'>Email</label>
                        <div className='lr-input-wrap'>
                          <span className='lr-input-icon' aria-hidden='true'><Mail size={16} /></span>
                          <input
                            id='login-email' type='email' placeholder='you@company.com'
                            className={`lr-input${formik.touched.email && formik.errors.email ? ' lr-input--error' : ''}`}
                            {...formik.getFieldProps('email')} name='email' autoComplete='email'
                          />
                        </div>
                        {formik.touched.email && formik.errors.email && (
                          <p className='lr-error-msg' role='alert'>{formik.errors.email}</p>
                        )}
                      </motion.div>

                      {/* Password */}
                      <motion.div className='lr-form-group' variants={itemVariants}>
                        <label className='lr-label' htmlFor='login-password'>Password</label>
                        <div className='lr-input-wrap'>
                          <span className='lr-input-icon' aria-hidden='true'><Lock size={16} /></span>
                          <input
                            id='login-password'
                            type={showPass ? 'text' : 'password'}
                            placeholder='••••••••'
                            className={`lr-input lr-input--pr${formik.touched.password && formik.errors.password ? ' lr-input--error' : ''}`}
                            autoComplete='current-password'
                            {...formik.getFieldProps('password')}
                          />
                          <button type='button' className='lr-toggle-btn'
                            onClick={() => setShowPass(p => !p)}
                            aria-label={showPass ? 'Hide password' : 'Show password'}
                          >
                            {showPass ? <EyeOff size={17} /> : <Eye size={17} />}
                          </button>
                        </div>
                        {formik.touched.password && formik.errors.password && (
                          <p className='lr-error-msg' role='alert'>{formik.errors.password}</p>
                        )}
                      </motion.div>

                      {/* Forgot password */}
                      <motion.div className='lr-forgot' variants={itemVariants}>
                        <Link to='/forgot-password'>I forgot my password</Link>
                      </motion.div>

                    </motion.div>
                  </motion.div>
                ) : (
                  <motion.div key='step-2fa' variants={stepVariants} initial='enter' animate='center' exit='exit'>
                    <motion.div variants={containerVariants} initial='hidden' animate='visible'>

                      {/* 2FA info */}
                      <motion.div className='lr-tfa-info' variants={itemVariants}>
                        <p className='lr-tfa-info__title'>
                          {selectedMethod === 'EMAIL'
                            ? `Code sent to ${emailDestination || 'your email'}`
                            : 'Open your authenticator app'}
                        </p>
                        <p className='lr-tfa-info__sub'>
                          Your password was accepted. One more step to verify it&apos;s you.
                        </p>
                      </motion.div>

                      {/* Code input */}
                      <motion.div className='lr-form-group' variants={itemVariants}>
                        <label className='lr-label' htmlFor='login-2fa-code'>
                          {selectedMethod === 'EMAIL' ? 'Email code' : 'Authenticator code'}
                        </label>
                        <div className='lr-input-wrap'>
                          <span className='lr-input-icon' aria-hidden='true'><ShieldCheck size={16} /></span>
                          <input
                            id='login-2fa-code' type='tel' placeholder='000000'
                            inputMode='numeric' autoComplete='one-time-code'
                            value={twoFactorCode}
                            onChange={e => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            className='lr-input'
                          />
                        </div>
                      </motion.div>

                      {/* 2FA links */}
                      <motion.div className='lr-link-actions' variants={itemVariants}>
                        {canUseEmailFallback && selectedMethod !== 'EMAIL' && (
                          <button type='button' className='lr-link-btn' onClick={handleSendEmailCode} disabled={loading}>
                            Can&apos;t access authenticator app? Send code by email.
                          </button>
                        )}
                        {selectedMethod === 'EMAIL' && (
                          <>
                            <button type='button' className='lr-link-btn'
                              onClick={() => { setSelectedMethod('APP'); setTwoFactorCode(''); formik.setStatus(null) }}
                              disabled={loading}>
                              Use authenticator app instead
                            </button>
                            <button type='button' className='lr-link-btn' onClick={handleSendEmailCode} disabled={loading}>
                              Resend email code
                            </button>
                          </>
                        )}
                        <button type='button' className='lr-link-btn lr-link-btn--muted'
                          onClick={() => { resetTwoFactorState(); formik.setStatus(null) }}
                          disabled={loading}>
                          Use another account
                        </button>
                      </motion.div>

                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Continue button */}
              <motion.button
                type='submit'
                id='kt_sign_in_submit'
                className='lr-btn-primary'
                disabled={isSubmitDisabled}
                whileHover={isSubmitDisabled || prefersReducedMotion ? {} : { scale: 1.02 }}
                whileTap={isSubmitDisabled || prefersReducedMotion ? {} : { scale: 0.98 }}
              >
                {loading ? (
                  <><span className='lr-spinner' aria-hidden='true' />Please wait…</>
                ) : (
                  isTwoFactorStep ? 'Verify and continue' : 'Continue →'
                )}
              </motion.button>

              {/* Passkey */}
              {!isTwoFactorStep && (
                <>
                  <div className='lr-divider'>or</div>
                  <motion.button
                    type='button' className='lr-btn-secondary'
                    onClick={handlePasskeySignIn} disabled={loading || !passkeySupported}
                    whileHover={loading || !passkeySupported || prefersReducedMotion ? {} : { scale: 1.01 }}
                    whileTap={loading || !passkeySupported || prefersReducedMotion ? {} : { scale: 0.99 }}
                  >
                    <Fingerprint size={18} aria-hidden='true' />
                    Sign in with passkey
                  </motion.button>
                </>
              )}

              {!isTwoFactorStep && !passkeySupported && (
                <p className='lr-passkey-note'>{getPasskeyUnavailableMessage()}</p>
              )}
            </form>
          </div>

          {/* ── Right: illustration ──────────────────────────────────────── */}
          <div className='lr-card-visual' aria-hidden='true'>
            <img src='/media/tfi/logo.webp' alt='' />
          </div>
        </motion.div>

        <div className='lr-footer'>{VERSION_APP}</div>
      </div>
    </div>
  )
}
